import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Container, Avatar, Typography, Card, CardContent, CardActionArea } from '@mui/material';

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { CircularProgress } from '@mui/material';
import HttpsIcon from '@mui/icons-material/Https';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { message } from 'antd';

import '../../Styles/Menu/PointOfSale.css'

const Billing = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check permissions on component mount
  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.error('No se encontró el token de autenticación.');
      setPermissionError(true);
      window.location.href = '/sign-in'; // Redirect to login if no token
      return;
    }
    setPermissionError(false);
    setIsLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const billingMenuOptions = [
    {
      title: 'Facturación de servicios',
      description: 'Facturar servicios.',
      path: `/billing/services-billing`,
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40, color: '#531dab', opacity: 0.8 }} />
    },
    {
      title: 'Gestionar facturas',
      description: 'Consultar las facturas emitidas.',
      path: `/billing/issued-invoices`,
      icon: <ReceiptIcon sx={{ fontSize: 40, color: '#531dab', opacity: 0.8 }} />
    },
    {
      title: 'Reporte de pagos diarios',
      description: 'Obtener un resumen de los pagos realizados durante el día.',
      path: `/billing/payment-report`,
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#531dab', opacity: 0.8 }} />
    },
    {
      title: 'Reporte de pagos mensuales',
      description: 'Obtener un resumen de los pagos realizados durante el mes.',
      path: `/billing/monthly-payment-report`,
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#531dab', opacity: 0.8 }} />
    },
    {
      title: 'Reporte de pagos anuales',
      description: 'Obtener un resumen de los pagos realizados durante el año.',
      path: `/billing/annual-payment-report`,
      icon: <AssessmentIcon sx={{ fontSize: 40, color: '#531dab', opacity: 0.8 }} />
    },
    {
      title: 'Reporte de facturas',
      description: 'Visualizar el reporte de facturas emitidas.',
      path: `/billing/invoice-report`,
      icon: <ReceiptIcon sx={{ fontSize: 40, color: '#531dab', opacity: 0.8 }} />
    },
    {
      title: 'Gestionar Notas de débito',
      description: 'Consultar y gestionar las notas de débito emitidas.',
      path: `/billing/list-debit-notes`,
      icon: <ReceiptLongIcon sx={{ fontSize: 40, color: '#531dab', opacity: 0.8 }} />
    },
    {
      title: 'Gestionar Notas de crédito',
      description: 'Consultar y gestionar las notas de crédito emitidas.',
      path: `/billing/list-credit-notes`,
      icon: <ReceiptLongIcon sx={{ fontSize: 40, color: '#531dab', opacity: 0.8 }} />
    }
  ];
  const cardStyle = {
    maxWidth: 275,
    height: 180,
    boxShadow: '0 4px 12px rgba(83, 29, 171, 0.15)',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(83, 29, 171, 0.25)',
    }
  };
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    setTimeout(() => {
      navigate(path)
    }, 100)
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <div className='pointofsale-main-container'>
      {contextHolder}
      <div className='pointofsale-container'>
        {billingMenuOptions.length > 0 && billingMenuOptions.map((option, index) => (
          <div className='cashflow-opt-container grid-obj' key={index}>
            <Card sx={cardStyle}>
              <CardActionArea sx={{ height: 180 }} onClick={() => handleNavigate(option.path)}>
                <CardContent sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: 150,
                  justifyContent: 'space-between',
                  padding: '20px'
                }}>
                  <div className='content-container'>
                    <Typography sx={{
                      color: '#531dab',
                      fontWeight: 'bold',
                      marginBottom: '8px'
                    }} gutterBottom variant="h5" component="div">
                      {option.title}
                    </Typography>
                    <Typography variant="body2" sx={{
                      color: 'text.secondary',
                      lineHeight: 1.4
                    }}>
                      {option.description}
                    </Typography>
                  </div>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {option.icon}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Billing;